import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CAMPAIGN_STATUS_LABEL,
  CHANNEL_KIND_LABEL,
  EXCLUSION_REASON_LABEL,
  NOTICE_SEND_POLICY_LABEL,
  PURPOSE_LABEL,
  type CampaignAudienceStatus,
  type NoticeDispatchRecord,
  type NoticeProgramRecord,
  type NoticeUnreachablePage,
} from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useConfirm } from '@/contexts/confirmContext';
import { CAMPAIGNS_HUB_PATH } from '../api/campaignPaths';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';

const STATUS_CLASS: Record<string, string> = {
  SENT: 'text-emerald-700',
  RESPONDED: 'text-emerald-700',
  EXCLUDED: 'text-amber-700',
  FAILED: 'text-destructive',
  SENDING: 'text-sky-700',
  QUEUED: 'text-muted-foreground',
  DEFERRED: 'text-muted-foreground',
};

export function NoticeProgramDetailPage() {
  const programId = String(useParams().programId ?? '').slice(0, 64);
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [program, setProgram] = useState<NoticeProgramRecord | null>(null);
  const [feed, setFeed] = useState<NoticeDispatchRecord[]>([]);
  const [unreachable, setUnreachable] = useState<NoticeUnreachablePage>({ items: [], total: 0 });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const [loaded, dispatches, missing] = await Promise.all([
      contactAgentApi.getNoticeProgram(programId),
      contactAgentApi.listNoticeDispatches(programId, { limit: 50 }),
      contactAgentApi.listNoticeUnreachable(programId, { limit: 50 }),
    ]);
    setProgram(loaded);
    setFeed(dispatches.items);
    setUnreachable(missing);
  }, [programId]);

  useEffect(() => {
    let cancelled = false;
    setError('');
    void reload().catch(err => {
      if (!cancelled) {
        setError(contactAgentError(err));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = async (action: () => Promise<unknown>, confirmText?: string) => {
    if (confirmText) {
      const ok = await confirm({ title: 'Confirmar', message: confirmText });
      if (!ok) {
        return;
      }
    }
    setBusy(true);
    setError('');
    try {
      await action();
      await reload();
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !program) {
    return (
      <ContactAgentPage>
        <ContactAgentPageHeader
          eyebrow='Campañas'
          backTo={`${CAMPAIGNS_HUB_PATH}?tab=dispatches`}
          title='Disparo'
          description={error}
        />
      </ContactAgentPage>
    );
  }

  if (!program) {
    return null;
  }

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        eyebrow='Campañas'
        backTo={`${CAMPAIGNS_HUB_PATH}?tab=dispatches`}
        title={program.name}
        description='Tu sistema avisa; acá solo se configura el mensaje y el canal.'
        actions={
          <ProgramActions
            program={program}
            busy={busy}
            onActivate={() =>
              void run(
                () => contactAgentApi.activateNoticeProgram(program.id),
                'Vas a activar este programa. A partir de ahora tu sistema puede disparar avisos. ¿Seguimos?',
              )
            }
            onPause={() => void run(() => contactAgentApi.pauseNoticeProgram(program.id))}
            onResume={() => void run(() => contactAgentApi.activateNoticeProgram(program.id))}
            onComplete={() =>
              void run(
                () => contactAgentApi.completeNoticeProgram(program.id),
                'Vas a completar el programa. No van a salir más avisos.',
              )
            }
            onDelete={() =>
              void run(
                async () => {
                  await contactAgentApi.deleteNoticeProgram(program.id);
                  navigate(`${CAMPAIGNS_HUB_PATH}?tab=dispatches`);
                },
                program.setupOrigin
                  ? 'Vas a eliminar este disparo y desengancharlo del evento Setup. Esta acción no se puede deshacer.'
                  : 'Vas a eliminar este disparo permanentemente. Esta acción no se puede deshacer.',
              )
            }
          />
        }
      />
      {error ? <p className='text-destructive'>{error}</p> : null}
      <div className='flex flex-wrap gap-2'>
        {program.setupOrigin ? (
          <Badge variant='secondary'>Origen: Setup · {program.setupOrigin.detectorLabel}</Badge>
        ) : null}
        <Badge variant='secondary'>{PURPOSE_LABEL[program.purpose]}</Badge>
        <Badge variant='outline'>{CHANNEL_KIND_LABEL[program.channelKind]}</Badge>
        <Badge variant='outline'>{NOTICE_SEND_POLICY_LABEL[program.sendPolicy]}</Badge>
        <Badge>{CAMPAIGN_STATUS_LABEL[program.status]}</Badge>
      </div>
      {unreachable.total > 0 ? (
        <UnreachablePanel
          programId={program.id}
          page={unreachable}
          onRetry={dispatchId =>
            void run(() => contactAgentApi.retryNoticeDispatch(program.id, dispatchId))
          }
          busy={busy}
        />
      ) : null}
      <Card className='rounded-xl'>
        <CardHeader>
          <CardTitle>Últimos disparos</CardTitle>
        </CardHeader>
        <CardContent>
          {feed.length === 0 ? (
            <p className='text-sm text-muted-foreground'>Cuando tu sistema avise un paquete, va a aparecer acá.</p>
          ) : (
            <ul className='divide-y'>
              {feed.map(item => (
                <li key={item.id} className='flex flex-wrap items-center justify-between gap-2 py-3 text-sm'>
                  <span className='text-muted-foreground'>
                    {new Date(item.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span>{item.maskedTo}</span>
                  <span className={STATUS_CLASS[item.status] ?? 'text-muted-foreground'}>
                    {statusLabel(item)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </ContactAgentPage>
  );
}

function statusLabel(item: NoticeDispatchRecord): string {
  if (item.status === 'EXCLUDED' && item.exclusionReason) {
    return EXCLUSION_REASON_LABEL[item.exclusionReason];
  }
  const labels: Record<CampaignAudienceStatus, string> = {
    PENDING: 'Pendiente',
    DEFERRED: 'En espera',
    QUEUED: 'En cola',
    SENDING: 'Enviando',
    SENT: 'Enviado',
    RESPONDED: 'Respondió',
    OPTED_OUT: 'Baja',
    EXCLUDED: 'Excluido',
    FAILED: 'Falló',
  };
  return labels[item.status];
}

function ProgramActions({
  program,
  busy,
  onActivate,
  onPause,
  onResume,
  onComplete,
  onDelete,
}: {
  program: NoticeProgramRecord;
  busy: boolean;
  onActivate: () => void;
  onPause: () => void;
  onResume: () => void;
  onComplete: () => void;
  onDelete: () => void;
}) {
  return (
    <div className='flex flex-wrap gap-2'>
      {program.status === 'DRAFT' ? (
        <Button disabled={busy} onClick={onActivate}>
          Activar
        </Button>
      ) : null}
      {program.status === 'ACTIVE' ? (
        <Button variant='outline' disabled={busy} onClick={onPause}>
          Pausar
        </Button>
      ) : null}
      {program.status === 'PAUSED' ? (
        <Button disabled={busy} onClick={onResume}>
          Reanudar
        </Button>
      ) : null}
      {program.status === 'ACTIVE' || program.status === 'PAUSED' ? (
        <Button variant='ghost' disabled={busy} onClick={onComplete}>
          Completar
        </Button>
      ) : null}
      <Button variant='destructive' disabled={busy} onClick={onDelete}>
        Eliminar
      </Button>
    </div>
  );
}

function UnreachablePanel({
  programId,
  page,
  onRetry,
  busy,
}: {
  programId: string;
  page: NoticeUnreachablePage;
  onRetry: (dispatchId: string) => void;
  busy: boolean;
}) {
  return (
    <Card className='rounded-xl border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'>
      <CardHeader className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div>
          <CardTitle>Les falta el dato de contacto</CardTitle>
          <p className='mt-1 text-sm text-muted-foreground'>
            Estos avisos no salieron. Completá el dato o pedile al sistema que vuelva a disparar.
          </p>
        </div>
        <Button type='button' variant='outline' size='sm' onClick={() => downloadNoticeCsv(programId, page.items)}>
          Exportar CSV
        </Button>
      </CardHeader>
      <CardContent>
        <div className='overflow-auto rounded-xl border bg-background'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left'>
                <th className='px-3 py-2'>Hora</th>
                <th className='px-3 py-2'>Destino</th>
                <th className='px-3 py-2'>Motivo</th>
                <th className='px-3 py-2' />
              </tr>
            </thead>
            <tbody>
              {page.items.map(item => (
                <tr key={item.id} className='border-b last:border-0'>
                  <td className='px-3 py-2 text-muted-foreground'>
                    {new Date(item.createdAt).toLocaleString('es-AR')}
                  </td>
                  <td className='px-3 py-2'>{item.maskedTo}</td>
                  <td className='px-3 py-2 text-muted-foreground'>
                    {item.exclusionReason ? EXCLUSION_REASON_LABEL[item.exclusionReason] : 'Excluido'}
                  </td>
                  <td className='px-3 py-2 text-right'>
                    {item.contactId ? (
                      <Button variant='ghost' size='sm' asChild>
                        <Link to={`/admin/contact-agent/contacts`}>Completar email/teléfono</Link>
                      </Button>
                    ) : null}
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      disabled={busy}
                      onClick={() => onRetry(item.id)}
                    >
                      Reintentar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function downloadNoticeCsv(programId: string, items: NoticeDispatchRecord[]): void {
  const header = 'occurrenceId,maskedTo,reason,createdAt\n';
  const body = items
    .map(item =>
      [item.occurrenceId, item.maskedTo, item.exclusionReason ?? '', item.createdAt]
        .map(value => `"${String(value).replace(/"/g, '""')}"`)
        .join(','),
    )
    .join('\n');
  const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `faltantes-disparo-${programId.slice(0, 8)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}
